/**
 * CRM adapter interface + typed error class.
 *
 * Every CRM integration (HubSpot, Attio, future Pipedrive/Salesforce) implements this
 * interface against the same `Deal` / `Owner` / `CustomField` types. The React layer
 * never imports adapter implementations directly — it goes through `getCrmAdapter()`
 * in `./index.ts`, which reads `tenant.config.json.crm.type`.
 *
 * Adapters running in the browser route every call through `/.netlify/functions/crm-proxy`
 * so tokens never leave the server. Adapters running inside Netlify Functions
 * (e.g. webhook handlers) call upstream APIs directly with decrypted tokens.
 */

import type {
  Contact,
  CreateContactInput,
  CreateDealInput,
  CrmObjectType,
  CrmType,
  CustomField,
  Deal,
  ListContactsOpts,
  ListContactsResult,
  ListDealsOpts,
  ListDealsResult,
  ListLeadsOpts,
  ListLeadsResult,
  ListMeetingsOpts,
  ListMeetingsResult,
  Owner,
  UpdateContactInput,
  UpdateDealPatch,
  CrmFilter,
} from './types';

export interface CrmAdapter {
  readonly type: CrmType;
  listDeals(opts?: ListDealsOpts): Promise<ListDealsResult>;
  /**
   * Fetch a single deal by id, populating the same shape as `listDeals` items
   * (stage name resolved, contact map populated, custom fields normalised).
   * Returns `null` on 404 so callers can render "deal gone" without a try/catch
   * around every refresh.
   *
   * HubSpot: GET /crm/v3/objects/deals/{id}?associations=contacts.
   * Attio:   GET /v2/objects/deals/records/{id}.
   */
  getDeal(id: string): Promise<Deal | null>;
  listOwners(): Promise<Owner[]>;
  listFilters(): Promise<CrmFilter[]>;
  listCustomFields(objectType: CrmObjectType): Promise<CustomField[]>;
  createDeal(input: CreateDealInput): Promise<Deal>;
  updateDeal(id: string, patch: UpdateDealPatch): Promise<Deal>;
  assignOwner(dealId: string, ownerId: string): Promise<void>;
  setCustomField(dealId: string, fieldName: string, value: unknown): Promise<void>;
  /**
   * Fetch upcoming + recent meetings from the CRM.
   *
   * HubSpot exposes /crm/v3/objects/meetings. Attio doesn't always expose a
   * meetings object — adapters MAY fall back to deals with an upcoming
   * `close_date`. Always normalises into the shared `CrmMeeting` shape.
   */
  listMeetings(opts?: ListMeetingsOpts): Promise<ListMeetingsResult>;

  // ── Contact CRUD ─────────────────────────────────────────────────────────
  // HubSpot: /crm/v3/objects/contacts (GET list w/ paging.next.after,
  //   GET /{id}, POST, PATCH /{id}, body = { properties }).
  // Attio:   /v2/objects/people/records — POST .../query for list (body
  //   { limit, offset }), GET /{record_id}, POST, PATCH /{record_id},
  //   body = { data: { values: ... } }. Attio stores emails/phones as arrays —
  //   adapters flatten to first item, full list preserved in customFields.
  listContacts(opts?: ListContactsOpts): Promise<ListContactsResult>;
  getContact(id: string): Promise<Contact | null>;
  createContact(input: CreateContactInput): Promise<Contact>;
  updateContact(id: string, patch: UpdateContactInput): Promise<Contact>;

  /**
   * Fetch contacts in early lifecycle stages (leads). Leads = contacts with no
   * associated deal, useful for prospecting. Adapters filter to the provided
   * lifecycle stages; default to ['subscriber', 'lead', 'marketingqualifiedlead']
   * for HubSpot. Attio lacks native lifecycle filtering — client-side filter
   * applied server-side when workspace lacks the attribute.
   */
  listLeads(opts?: ListLeadsOpts): Promise<ListLeadsResult>;
}

export type AdapterErrorCode =
  | 'NOT_AUTHENTICATED'      // No credential row found
  | 'TOKEN_EXPIRED'          // Refresh-needed and refresh failed
  | 'RATE_LIMITED'           // 429 from upstream
  | 'NOT_FOUND'              // 404 — record missing
  | 'VALIDATION_FAILED'      // 400 — bad payload
  | 'PERMISSION_DENIED'      // 403 — scope missing
  | 'UPSTREAM_ERROR'         // 5xx from upstream
  | 'NETWORK_ERROR'          // fetch failed before response
  | 'PROXY_ERROR'            // crm-proxy itself errored
  | 'UNKNOWN';

export class AdapterError extends Error {
  readonly code: AdapterErrorCode;
  readonly status: number | null;
  readonly upstreamBody: unknown;

  constructor(
    code: AdapterErrorCode,
    message: string,
    opts: { status?: number; upstreamBody?: unknown } = {},
  ) {
    super(message);
    this.name = 'AdapterError';
    this.code = code;
    this.status = opts.status ?? null;
    this.upstreamBody = opts.upstreamBody ?? null;
  }
}

/** Best-effort classifier — turns an HTTP status + body into a typed code. */
export function classifyHttpError(status: number, body: unknown): AdapterErrorCode {
  if (status === 401) return 'TOKEN_EXPIRED';
  if (status === 403) return 'PERMISSION_DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 400 && status < 500) return 'VALIDATION_FAILED';
  if (status >= 500) return 'UPSTREAM_ERROR';
  void body;
  return 'UNKNOWN';
}
